import apiRoutes from '../../../config/api/routes';
import useCachedFetch from './useCachedFetch';
import { useCallback, useEffect, useState } from 'react';
import { BookWithChapters, BookWithChaptersResponse, UrlParams } from '../types';
import { useParams } from 'react-router';
import { IBookChapter } from 'src/config/api/models/Chapter';
import { Book } from 'src/config/api/models/Book';
import { useAppDispatch } from './useAppDispatch';
import { setCurrentBook } from '../slices/book/bookEntity';

const useFullBook = (bookId?: string): BookWithChaptersResponse => {
  let { bookId: bookIdToUse } = useParams<UrlParams>();
  const dispatch = useAppDispatch();

  if (bookId) {
    bookIdToUse = bookId;
  }

  const [data, setData] = useState<BookWithChapters | undefined>(undefined);
  const bookFetch = useCachedFetch<{ data: Book }>(
    ['book', bookIdToUse as string],
    apiRoutes.book(bookIdToUse as string),
    {
      options: {
        enabled: !!bookIdToUse && data == null,
      },
    }
  );

  const chaptersFetch = useCachedFetch<{ data: IBookChapter[] }>(
    ['chapters', bookIdToUse as string],
    apiRoutes.chapters(bookIdToUse as string),
    {
      options: {
        enabled: !!bookIdToUse && data == null,
      },
    }
  );

  const refetch = useCallback(() => {
    setData(undefined);
    bookFetch.refetch().then();
    chaptersFetch.refetch().then();
  }, [bookFetch, chaptersFetch]);

  useEffect(() => {
    if (
      bookFetch.isSuccess &&
      chaptersFetch.isSuccess &&
      !!bookFetch.data &&
      !!chaptersFetch.data
    ) {
      dispatch(setCurrentBook(bookFetch.data.data));
      setData({ ...bookFetch.data.data, chapters: chaptersFetch.data.data });
    }
  }, [
    bookFetch.data,
    bookFetch.isSuccess,
    chaptersFetch.data,
    chaptersFetch.isSuccess,
    setData,
    dispatch,
  ]);

  const pending = bookFetch.isLoading || chaptersFetch.isLoading;
  const error = bookFetch.isError || chaptersFetch.isError;
  const success = bookFetch.isSuccess && chaptersFetch.isSuccess;

  return { pending, error, success, data, refetch };
};

export default useFullBook;
